import { useState, useRef, useEffect, useCallback } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ImageAnalyzer, type ImageAnalysis } from '../utils/imageAnalyzer';

interface ProcessingSettings {
  brightness: number;
  contrast: number;
  sharpness: number;
  rotation: number;
  saturation: number;
}


interface CornerPoint {
  x: number;
  y: number;
}

interface PerspectivePoints {
  topLeft: CornerPoint;
  topRight: CornerPoint;
  bottomLeft: CornerPoint;
  bottomRight: CornerPoint;
}


export default function ImageProcessingTest() {
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [settings, setSettings] = useState<ProcessingSettings>({
    brightness: 0,
    contrast: 0,
    sharpness: 0,
    rotation: 0,
    saturation: 0,
  });
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [fullscreenType, setFullscreenType] = useState<'original' | 'processed' | null>(null);
  const [showPerspectiveEditor, setShowPerspectiveEditor] = useState(false);
  const [perspectivePoints, setPerspectivePoints] = useState<PerspectivePoints | null>(null);
  const [draggedCorner, setDraggedCorner] = useState<keyof PerspectivePoints | null>(null);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number } | null>(null);
  const [imageAnalysis, setImageAnalysis] = useState<ImageAnalysis | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showAutoSuggestions, setShowAutoSuggestions] = useState(false);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const perspectiveCanvasRef = useRef<HTMLCanvasElement>(null);
  const perspectivePreviewRef = useRef<HTMLCanvasElement>(null);
  const finalOutputRef = useRef<HTMLCanvasElement>(null);



  // 處理圖片上傳
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setOriginalFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      setOriginalImage(event.target?.result as string);
      // 重設透視校正點
      setPerspectivePoints(null);
      // 重設分析結果
      setImageAnalysis(null);
      setShowAutoSuggestions(false);
    };
    reader.readAsDataURL(file);
  }, []);

  // 應用圖片處理效果
  const applyProcessing = useCallback(() => {
    if (!originalImage || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      // 設定 canvas 大小
      canvas.width = img.width;
      canvas.height = img.height;

      // 清除畫布
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 套用旋轉
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((settings.rotation * Math.PI) / 180);
      ctx.translate(-canvas.width / 2, -canvas.height / 2);

      // 繪製圖片
      ctx.drawImage(img, 0, 0);
      ctx.restore();

      // 取得圖片數據進行處理
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // 處理每個像素
      for (let i = 0; i < data.length; i += 4) {
        let r = data[i];
        let g = data[i + 1];
        let b = data[i + 2];

        // 亮度調整
        r += settings.brightness;
        g += settings.brightness;
        b += settings.brightness;

        // 對比度調整
        const contrastFactor = (259 * (settings.contrast + 255)) / (255 * (259 - settings.contrast));
        r = contrastFactor * (r - 128) + 128;
        g = contrastFactor * (g - 128) + 128;
        b = contrastFactor * (b - 128) + 128;

        // 飽和度調整
        const gray = 0.299 * r + 0.587 * g + 0.114 * b;
        r = gray + (r - gray) * (1 + settings.saturation / 100);
        g = gray + (g - gray) * (1 + settings.saturation / 100);
        b = gray + (b - gray) * (1 + settings.saturation / 100);

        // 確保值在 0-255 範圍內
        data[i] = Math.max(0, Math.min(255, r));
        data[i + 1] = Math.max(0, Math.min(255, g));
        data[i + 2] = Math.max(0, Math.min(255, b));
      }

      // 銳化處理
      let finalImageData = imageData;
      if (settings.sharpness > 0) {
        finalImageData = applySharpen(imageData, settings.sharpness);
      }


      ctx.putImageData(finalImageData, 0, 0);
    };
    img.src = originalImage;
  }, [originalImage, settings]);

  // 銳化濾鏡
  const applySharpen = (imageData: ImageData, intensity: number): ImageData => {
    const data = imageData.data;
    const width = imageData.width;
    const height = imageData.height;
    const output = new Uint8ClampedArray(data);

    // 銳化核心
    const kernel = [
      0, -intensity / 100, 0,
      -intensity / 100, 1 + 4 * intensity / 100, -intensity / 100,
      0, -intensity / 100, 0
    ];

    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        for (let c = 0; c < 3; c++) {
          let sum = 0;
          for (let ky = -1; ky <= 1; ky++) {
            for (let kx = -1; kx <= 1; kx++) {
              const idx = ((y + ky) * width + (x + kx)) * 4 + c;
              const kernelIdx = (ky + 1) * 3 + (kx + 1);
              sum += data[idx] * kernel[kernelIdx];
            }
          }
          const outputIdx = (y * width + x) * 4 + c;
          output[outputIdx] = Math.max(0, Math.min(255, sum));
        }
      }
    }

    return new ImageData(output, width, height);
  };


  // 當設定改變時重新處理圖片
  useEffect(() => {
    if (originalImage) {
      applyProcessing();
    }
  }, [originalImage, settings, applyProcessing]);



  // 重設所有設定
  const resetSettings = () => {
    setSettings({
      brightness: 0,
      contrast: 0,
      sharpness: 0,
      rotation: 0,
      saturation: 0,
    });
    setShowAutoSuggestions(false);
  };

  // 下載處理後的圖片
  const downloadProcessedImage = () => {
    let canvasToDownload = canvasRef.current;
    
    // 如果有最終輸出且透視校正開啟，使用最終輸出
    if (finalOutputRef.current && showPerspectiveEditor) {
      canvasToDownload = finalOutputRef.current;
    }
    
    if (!canvasToDownload) return;
    
    const link = document.createElement('a');
    const suffix = '_processed';
    link.download = `${originalFile?.name?.replace(/\.[^/.]+$/, "") || 'image'}${suffix}.jpg`;
    link.href = canvasToDownload.toDataURL();
    link.click();
  };

  // 開啟全螢幕檢視
  const openFullscreen = (type: 'original' | 'processed') => {
    if (type === 'original' && originalImage) {
      setFullscreenImage(originalImage);
      setFullscreenType('original');
    } else if (type === 'processed') {
      let canvasToView = canvasRef.current;
      
      // 如果有最終輸出且透視校正開啟，使用最終輸出
      if (finalOutputRef.current && showPerspectiveEditor) {
        canvasToView = finalOutputRef.current;
      }
      
      if (canvasToView) {
        setFullscreenImage(canvasToView.toDataURL());
        setFullscreenType('processed');
      }
    }
  };

  // 關閉全螢幕檢視
  const closeFullscreen = () => {
    setFullscreenImage(null);
    setFullscreenType(null);
    document.body.style.overflow = 'auto';
  };

  // 鍵盤事件處理
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && fullscreenImage) {
        closeFullscreen();
      }
    };

    if (fullscreenImage) {
      document.addEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
      document.body.style.overflow = 'auto';
    };
  }, [fullscreenImage]);

  // 初始化透視校正點
  const initializePerspectivePoints = useCallback(() => {
    // 延遲執行以確保所有 canvas 都已渲染
    setTimeout(() => {
      if (!canvasRef.current || !perspectiveCanvasRef.current) return;
      
      const canvas = perspectiveCanvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      // 使用處理後的圖片
      const processedCanvas = canvasRef.current;
      
      // 設定 canvas 尺寸
      canvas.width = processedCanvas.width;
      canvas.height = processedCanvas.height;
      
      // 複製處理後的圖片到透視校正 canvas
      ctx.drawImage(processedCanvas, 0, 0);
      
      const margin = 0.1; // 10% 邊距
      setPerspectivePoints({
        topLeft: { x: canvas.width * margin, y: canvas.height * margin },
        topRight: { x: canvas.width * (1 - margin), y: canvas.height * margin },
        bottomLeft: { x: canvas.width * margin, y: canvas.height * (1 - margin) },
        bottomRight: { x: canvas.width * (1 - margin), y: canvas.height * (1 - margin) }
      });
    }, 100);
  }, []);


  // 應用透視校正 - 加入防抖
  const applyPerspectiveCorrection = useCallback(() => {
    if (!perspectiveCanvasRef.current || !perspectivePoints || !perspectivePreviewRef.current) return;

    const sourceCanvas = perspectiveCanvasRef.current;
    const canvas = perspectivePreviewRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 動態計算輸出尺寸，保持選中區域的長寬比
    const selectedRegionWidth = Math.max(
      Math.abs(perspectivePoints.topRight.x - perspectivePoints.topLeft.x),
      Math.abs(perspectivePoints.bottomRight.x - perspectivePoints.bottomLeft.x)
    );
    const selectedRegionHeight = Math.max(
      Math.abs(perspectivePoints.bottomLeft.y - perspectivePoints.topLeft.y),
      Math.abs(perspectivePoints.bottomRight.y - perspectivePoints.topRight.y)
    );
    
    // 計算長寬比
    const aspectRatio = selectedRegionWidth / selectedRegionHeight;
    
    // 設定最大尺寸限制，但保持長寬比
    const maxSize = 1200;
    let outputWidth, outputHeight;
    
    if (aspectRatio > 1) {
      // 寬度較大
      outputWidth = Math.min(maxSize, selectedRegionWidth);
      outputHeight = outputWidth / aspectRatio;
    } else {
      // 高度較大
      outputHeight = Math.min(maxSize, selectedRegionHeight);
      outputWidth = outputHeight * aspectRatio;
    }
    
    canvas.width = Math.round(outputWidth);
    canvas.height = Math.round(outputHeight);

    // 目標矩形（校正後的矩形）
    const destPoints = {
      topLeft: { x: 0, y: 0 },
      topRight: { x: canvas.width, y: 0 },
      bottomLeft: { x: 0, y: canvas.height },
      bottomRight: { x: canvas.width, y: canvas.height }
    };

    // 簡化的透視校正：使用四邊形到矩形的映射
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // 創建臨時圖片從 canvas
    const tempImg = new Image();
    tempImg.onload = () => {
      // 實現透視變換
      drawPerspectiveImage(ctx, tempImg, perspectivePoints, destPoints, canvas.width, canvas.height);
      
      // 透視校正完成後，將結果複製到最終輸出 canvas
      setTimeout(() => {
        if (finalOutputRef.current) {
          const finalCtx = finalOutputRef.current.getContext('2d');
          if (finalCtx) {
            finalOutputRef.current.width = canvas.width;
            finalOutputRef.current.height = canvas.height;
            finalCtx.drawImage(canvas, 0, 0);
          }
        }
      }, 100);
    };
    tempImg.src = sourceCanvas.toDataURL();
  }, [perspectivePoints]);

  // 防抖的透視校正應用
  const debouncedApplyPerspectiveCorrection = useCallback(() => {
    const timeoutId = setTimeout(() => {
      applyPerspectiveCorrection();
    }, 150); // 150ms 防抖
    return () => clearTimeout(timeoutId);
  }, [applyPerspectiveCorrection]);

  // 繪製透視校正後的圖片
  const drawPerspectiveImage = (
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    src: PerspectivePoints,
    _dst: PerspectivePoints,
    width: number,
    height: number
  ) => {
    // 簡化版透視變換：使用雙線性插值
    const imageData = ctx.createImageData(width, height);
    
    // 創建臨時 canvas 來獲取原圖像數據
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d')!;
    tempCanvas.width = img.width;
    tempCanvas.height = img.height;
    tempCtx.drawImage(img, 0, 0);
    const srcImageData = tempCtx.getImageData(0, 0, img.width, img.height);
    
    // 對每個輸出像素進行反向映射
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        // 計算在原圖中的對應位置（簡化版）
        const u = x / width;
        const v = y / height;
        
        // 雙線性插值計算原圖坐標
        const srcX = bilinearInterpolate(
          src.topLeft.x, src.topRight.x,
          src.bottomLeft.x, src.bottomRight.x,
          u, v
        );
        
        const srcY = bilinearInterpolate(
          src.topLeft.y, src.topRight.y,
          src.bottomLeft.y, src.bottomRight.y,
          u, v
        );
        
        // 確保坐標在圖片範圍內
        if (srcX >= 0 && srcX < img.width && srcY >= 0 && srcY < img.height) {
          const srcIndex = (Math.floor(srcY) * img.width + Math.floor(srcX)) * 4;
          const dstIndex = (y * width + x) * 4;
          
          imageData.data[dstIndex] = srcImageData.data[srcIndex];     // R
          imageData.data[dstIndex + 1] = srcImageData.data[srcIndex + 1]; // G
          imageData.data[dstIndex + 2] = srcImageData.data[srcIndex + 2]; // B
          imageData.data[dstIndex + 3] = 255; // A
        }
      }
    }
    
    ctx.putImageData(imageData, 0, 0);
  };

  // 雙線性插值
  const bilinearInterpolate = (
    tl: number, tr: number,
    bl: number, br: number,
    u: number, v: number
  ): number => {
    const top = tl * (1 - u) + tr * u;
    const bottom = bl * (1 - u) + br * u;
    return top * (1 - v) + bottom * v;
  };


  // 重新繪製透視校正畫布
  const redrawPerspectiveCanvas = useCallback(() => {
    if (!canvasRef.current || !perspectiveCanvasRef.current || !perspectivePoints) return;
    
    const canvas = perspectiveCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const sourceCanvas = canvasRef.current;
    
    // 清除畫布
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // 繪製處理後的圖片
    ctx.drawImage(sourceCanvas, 0, 0, canvas.width, canvas.height);
    
    // 如果正在透視校正，添加淡淡的遮罩效果但保持圖片清晰
    if (showPerspectiveEditor && perspectivePoints) {
      ctx.save();
      
      // 只在選中區域外添加輕微的遮罩，選中區域內保持原圖亮度
      ctx.globalAlpha = 0.2;
      ctx.fillStyle = 'rgba(0, 0, 0, 1)';
      
      // 使用 evenodd 填充規則創建"甜甜圈"形狀的遮罩
      ctx.beginPath();
      // 外部矩形（整個畫布）
      ctx.rect(0, 0, canvas.width, canvas.height);
      // 內部四邊形（選中區域，逆時針方向以創建洞）
      ctx.moveTo(perspectivePoints.topLeft.x, perspectivePoints.topLeft.y);
      ctx.lineTo(perspectivePoints.bottomLeft.x, perspectivePoints.bottomLeft.y);
      ctx.lineTo(perspectivePoints.bottomRight.x, perspectivePoints.bottomRight.y);
      ctx.lineTo(perspectivePoints.topRight.x, perspectivePoints.topRight.y);
      ctx.closePath();
      
      ctx.fill('evenodd');
      
      ctx.restore();
    }
    
    // 繪製邊界線
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(perspectivePoints.topLeft.x, perspectivePoints.topLeft.y);
    ctx.lineTo(perspectivePoints.topRight.x, perspectivePoints.topRight.y);
    ctx.lineTo(perspectivePoints.bottomRight.x, perspectivePoints.bottomRight.y);
    ctx.lineTo(perspectivePoints.bottomLeft.x, perspectivePoints.bottomLeft.y);
    ctx.closePath();
    ctx.stroke();
    
    // 繪製角點 - 增大尺寸讓它們更容易點擊
    Object.values(perspectivePoints).forEach(point => {
      // 外圈
      ctx.fillStyle = '#ef4444';
      ctx.beginPath();
      ctx.arc(point.x, point.y, 12, 0, 2 * Math.PI);
      ctx.fill();
      
      // 內圈
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(point.x, point.y, 8, 0, 2 * Math.PI);
      ctx.fill();
      
      // 中心點
      ctx.fillStyle = '#ef4444';
      ctx.beginPath();
      ctx.arc(point.x, point.y, 4, 0, 2 * Math.PI);
      ctx.fill();
    });
  }, [perspectivePoints, showPerspectiveEditor]);

  // 當透視點改變時重新繪製和應用校正（使用防抖）
  useEffect(() => {
    if (perspectivePoints && showPerspectiveEditor) {
      redrawPerspectiveCanvas();
      const cleanup = debouncedApplyPerspectiveCorrection();
      return cleanup;
    }
  }, [perspectivePoints, redrawPerspectiveCanvas, debouncedApplyPerspectiveCorrection, showPerspectiveEditor]);

  // 當設定改變且透視編輯器開啟時，更新透視校正畫布（減少頻率）
  useEffect(() => {
    if (showPerspectiveEditor && perspectivePoints && canvasRef.current) {
      // 延遲執行以確保處理後的圖片已更新，並增加延遲時間減少重繪頻率
      const timer = setTimeout(() => {
        redrawPerspectiveCanvas();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [settings, showPerspectiveEditor, perspectivePoints, redrawPerspectiveCanvas]);

  // 添加全域滑鼠事件監聽器
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!draggedCorner || !perspectivePoints || !perspectiveCanvasRef.current || !dragOffset) return;
      
      const canvas = perspectiveCanvasRef.current;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      
      // 計算滑鼠位置並扣除偏移量
      const mouseX = (e.clientX - rect.left) * scaleX;
      const mouseY = (e.clientY - rect.top) * scaleY;
      
      const x = Math.max(0, Math.min(canvas.width, mouseX - dragOffset.x));
      const y = Math.max(0, Math.min(canvas.height, mouseY - dragOffset.y));
      
      setPerspectivePoints(prev => ({
        ...prev!,
        [draggedCorner]: { x, y }
      }));
    };

    const handleGlobalMouseUp = () => {
      setDraggedCorner(null);
      setDragOffset(null);
    };

    if (draggedCorner) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [draggedCorner, perspectivePoints, dragOffset]);

  // 自動分析圖片
  const analyzeImageQuality = useCallback(async () => {
    if (!originalFile) return;
    
    setIsAnalyzing(true);
    try {
      const analyzer = new ImageAnalyzer();
      const analysis = await analyzer.analyzeImage(originalFile);
      setImageAnalysis(analysis);
      setShowAutoSuggestions(true);
    } catch (error) {
      console.error('圖片分析失敗:', error);
    } finally {
      setIsAnalyzing(false);
    }
  }, [originalFile]);

  // 套用自動建議
  const applyAutoSuggestions = useCallback(() => {
    if (!imageAnalysis) return;
    
    const { recommendations } = imageAnalysis;
    setSettings(() => ({
      brightness: recommendations.brightness,
      contrast: recommendations.contrast,
      sharpness: recommendations.sharpness,
      rotation: recommendations.rotation,
      saturation: recommendations.saturation,
    }));
  }, [imageAnalysis]);

  // 重設所有設定（移除這個重複定義，保留原本的）

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">
          AI 作文圖片優化工具
        </h1>
        
        {/* 流程說明 */}
        {!originalImage && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <p className="text-blue-800 text-center">
              📸 上傳作文照片 → 🤖 AI 自動分析 → 🎛️ 手動微調 → 📐 透視校正 → 💾 下載優化圖片
            </p>
          </div>
        )}

        {/* 檔案上傳區域 */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">上傳測試圖片</h2>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="bg-slate-700 text-white px-6 py-3 rounded-lg hover:bg-slate-800 transition-colors"
            >
              選擇圖片
            </button>
            <p className="text-gray-500 mt-2">支援 JPG、PNG 格式</p>
          </div>
        </div>

        {originalImage && (
          <>
            {/* 進度指示器 */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-8">
              <div className="flex items-center justify-center space-x-4">
                <div className={`flex items-center space-x-2 ${imageAnalysis ? 'text-green-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${imageAnalysis ? 'bg-green-100' : 'bg-gray-100'}`}>
                    1
                  </div>
                  <span className="text-sm">分析完成</span>
                </div>
                <div className="w-8 h-0.5 bg-gray-300"></div>
                <div className={`flex items-center space-x-2 ${settings.brightness !== 0 || settings.contrast !== 0 ? 'text-green-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${settings.brightness !== 0 || settings.contrast !== 0 ? 'bg-green-100' : 'bg-gray-100'}`}>
                    2
                  </div>
                  <span className="text-sm">參數調整</span>
                </div>
                <div className="w-8 h-0.5 bg-gray-300"></div>
                <div className={`flex items-center space-x-2 ${showPerspectiveEditor ? 'text-green-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${showPerspectiveEditor ? 'bg-green-100' : 'bg-gray-100'}`}>
                    3
                  </div>
                  <span className="text-sm">透視校正</span>
                </div>
              </div>
            </div>

            {/* 控制面板 */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">處理參數</h2>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={analyzeImageQuality}
                    disabled={isAnalyzing}
                    className="bg-slate-600 text-white px-4 py-2 rounded hover:bg-slate-700 disabled:bg-gray-400 transition-colors"
                  >
                    {isAnalyzing ? '分析中...' : '🤖 自動分析'}
                  </button>
                  <button
                    onClick={() => {
                      if (!showPerspectiveEditor) {
                        initializePerspectivePoints();
                      }
                      setShowPerspectiveEditor(!showPerspectiveEditor);
                    }}
                    className={`px-4 py-2 rounded transition-colors ${
                      showPerspectiveEditor 
                        ? 'bg-slate-500 text-white hover:bg-slate-600' 
                        : 'bg-slate-700 text-white hover:bg-slate-800'
                    }`}
                  >
                    {showPerspectiveEditor ? '關閉透視校正' : '透視校正'}
                  </button>
                  <button
                    onClick={resetSettings}
                    className="bg-slate-500 text-white px-4 py-2 rounded hover:bg-slate-600 transition-colors"
                  >
                    重設
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* 亮度調整 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    亮度: {settings.brightness}
                  </label>
                  <input
                    type="range"
                    min="-100"
                    max="100"
                    value={settings.brightness}
                    onChange={(e) => setSettings(prev => ({ ...prev, brightness: Number(e.target.value) }))}
                    className="w-full"
                  />
                </div>

                {/* 對比度調整 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    對比度: {settings.contrast}
                  </label>
                  <input
                    type="range"
                    min="-100"
                    max="100"
                    value={settings.contrast}
                    onChange={(e) => setSettings(prev => ({ ...prev, contrast: Number(e.target.value) }))}
                    className="w-full"
                  />
                </div>

                {/* 銳化 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    銳化: {settings.sharpness}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={settings.sharpness}
                    onChange={(e) => setSettings(prev => ({ ...prev, sharpness: Number(e.target.value) }))}
                    className="w-full"
                  />
                </div>

                {/* 旋轉 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    旋轉: {settings.rotation}°
                  </label>
                  <input
                    type="range"
                    min="-45"
                    max="45"
                    value={settings.rotation}
                    onChange={(e) => setSettings(prev => ({ ...prev, rotation: Number(e.target.value) }))}
                    className="w-full"
                  />
                </div>

                {/* 飽和度 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    飽和度: {settings.saturation}
                  </label>
                  <input
                    type="range"
                    min="-100"
                    max="100"
                    value={settings.saturation}
                    onChange={(e) => setSettings(prev => ({ ...prev, saturation: Number(e.target.value) }))}
                    className="w-full"
                  />
                </div>

              </div>
            </div>

            {/* 自動分析結果 */}
            {showAutoSuggestions && imageAnalysis && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                <h2 className="text-xl font-semibold mb-4">🤖 AI 分析結果</h2>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* 圖片品質評估 */}
                  <div>
                    <h3 className="text-lg font-medium mb-3">品質評估</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span>整體品質：</span>
                        <span className={`px-2 py-1 rounded text-sm font-medium ${
                          imageAnalysis.overallQuality === 'excellent' ? 'bg-green-100 text-green-800' :
                          imageAnalysis.overallQuality === 'good' ? 'bg-blue-100 text-blue-800' :
                          imageAnalysis.overallQuality === 'fair' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {imageAnalysis.overallQuality === 'excellent' ? '優秀' :
                           imageAnalysis.overallQuality === 'good' ? '良好' :
                           imageAnalysis.overallQuality === 'fair' ? '普通' : '需改善'}
                        </span>
                      </div>
                      
                      <div className="text-sm space-y-1">
                        <div>亮度: {Math.round(imageAnalysis.brightness)}</div>
                        <div>對比度: {Math.round(imageAnalysis.contrast)}</div>
                        <div>清晰度: {Math.round(imageAnalysis.sharpness)}</div>
                        <div className={`${Math.abs(imageAnalysis.detectedAngle) > 5 ? 'text-red-600 font-medium' : ''}`}>
                          檢測角度: {imageAnalysis.detectedAngle.toFixed(1)}°
                        </div>
                      </div>
                      
                      {imageAnalysis.issues.length > 0 && (
                        <div>
                          <h4 className="font-medium text-red-700 mb-2">發現的問題：</h4>
                          <ul className="text-sm text-red-600 space-y-1">
                            {imageAnalysis.issues.map((issue, index) => (
                              <li key={index}>• {issue}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* 自動調整建議 */}
                  <div>
                    <h3 className="text-lg font-medium mb-3">調整建議</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span>建議信心度：</span>
                        <span className={`px-2 py-1 rounded text-sm font-medium ${
                          imageAnalysis.recommendations.confidence >= 80 ? 'bg-green-100 text-green-800' :
                          imageAnalysis.recommendations.confidence >= 60 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {imageAnalysis.recommendations.confidence}%
                        </span>
                      </div>
                      
                      <div className="text-sm space-y-1">
                        {imageAnalysis.recommendations.brightness !== 0 && (
                          <div>亮度調整: {imageAnalysis.recommendations.brightness > 0 ? '+' : ''}{imageAnalysis.recommendations.brightness}</div>
                        )}
                        {imageAnalysis.recommendations.contrast !== 0 && (
                          <div>對比度調整: {imageAnalysis.recommendations.contrast > 0 ? '+' : ''}{imageAnalysis.recommendations.contrast}</div>
                        )}
                        {imageAnalysis.recommendations.sharpness !== 0 && (
                          <div>清晰度調整: {imageAnalysis.recommendations.sharpness > 0 ? '+' : ''}{imageAnalysis.recommendations.sharpness}</div>
                        )}
                        {imageAnalysis.recommendations.saturation !== 0 && (
                          <div>飽和度調整: {imageAnalysis.recommendations.saturation > 0 ? '+' : ''}{imageAnalysis.recommendations.saturation}</div>
                        )}
                        {imageAnalysis.recommendations.rotation !== 0 && (
                          <div className="font-medium text-blue-700">
                            角度校正: {imageAnalysis.recommendations.rotation > 0 ? '+' : ''}{imageAnalysis.recommendations.rotation.toFixed(1)}°
                          </div>
                        )}
                      </div>
                      
                      <button
                        onClick={applyAutoSuggestions}
                        className="w-full bg-slate-600 text-white px-4 py-2 rounded hover:bg-slate-700 transition-colors mt-4"
                      >
                        套用建議設定
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 透視校正編輯器 */}
            {showPerspectiveEditor && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                <h2 className="text-xl font-semibold mb-4">透視校正編輯器</h2>
                <p className="text-gray-600 mb-4">拖拽四個角點來調整文件邊界，系統會自動校正透視角度</p>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* 編輯區域 */}
                  <div>
                    <h3 className="text-lg font-medium mb-3 text-center">調整邊界</h3>
                    <div className="border rounded-lg overflow-hidden bg-gray-100">
                      <canvas
                        ref={perspectiveCanvasRef}
                        className="w-full h-auto max-h-[600px] object-contain"
                        style={{ cursor: draggedCorner ? 'grabbing' : 'grab' }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          // 檢測點擊的是哪個角點
                          if (!perspectivePoints || !perspectiveCanvasRef.current) return;
                          
                          const canvas = perspectiveCanvasRef.current;
                          const rect = canvas.getBoundingClientRect();
                          const scaleX = canvas.width / rect.width;
                          const scaleY = canvas.height / rect.height;
                          
                          const x = (e.clientX - rect.left) * scaleX;
                          const y = (e.clientY - rect.top) * scaleY;
                          
                          // 檢測最近的角點
                          const corners = Object.entries(perspectivePoints) as [keyof PerspectivePoints, CornerPoint][];
                          let closestCorner: keyof PerspectivePoints | null = null;
                          let minDistance = Infinity;
                          const clickThreshold = 150; // 點擊檢測範圍
                          
                          corners.forEach(([corner, point]) => {
                            const distance = Math.sqrt(Math.pow(x - point.x, 2) + Math.pow(y - point.y, 2));
                            if (distance < minDistance) {
                              minDistance = distance;
                              closestCorner = corner;
                            }
                          });
                          
                          // 只有當最近距離小於閾值時才選中
                          if (minDistance > clickThreshold) {
                            closestCorner = null;
                          }
                          
                          if (closestCorner && perspectivePoints) {
                            // 計算滑鼠與角點的偏移量
                            const cornerPoint = perspectivePoints[closestCorner] as CornerPoint;
                            const offsetX = x - cornerPoint.x;
                            const offsetY = y - cornerPoint.y;
                            
                            setDraggedCorner(closestCorner);
                            setDragOffset({ x: offsetX, y: offsetY });
                          }
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 text-center mt-2">
                      點擊並拖拽紅色角點來調整文件邊界
                    </p>
                  </div>
                  
                  {/* 校正後預覽 */}
                  <div>
                    <h3 className="text-lg font-medium mb-3 text-center">校正後預覽</h3>
                    <div className="border rounded-lg overflow-hidden bg-gray-100">
                      <canvas
                        ref={perspectivePreviewRef}
                        className="w-full h-auto max-h-[600px] object-contain"
                        style={{ display: perspectivePoints ? 'block' : 'none' }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 text-center mt-2">
                      校正後的矩形化圖片
                    </p>
                  </div>
                </div>
                
                <div className="mt-4 flex justify-center gap-4">
                  <button
                    onClick={initializePerspectivePoints}
                    className="bg-slate-500 text-white px-4 py-2 rounded hover:bg-slate-600 transition-colors"
                  >
                    重設角點
                  </button>
                  <button
                    onClick={() => {
                      if (perspectivePreviewRef.current) {
                        const link = document.createElement('a');
                        link.download = `corrected_${originalFile?.name || 'image.jpg'}`;
                        link.href = perspectivePreviewRef.current.toDataURL();
                        link.click();
                      }
                    }}
                    className="bg-slate-700 text-white px-4 py-2 rounded hover:bg-slate-800 transition-colors"
                  >
                    下載最終圖片
                  </button>
                </div>
              </div>
            )}


            {/* 圖片對比顯示 */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">處理結果對比</h2>
                {!showPerspectiveEditor && (
                  <button
                    onClick={downloadProcessedImage}
                    className="bg-slate-700 text-white px-4 py-2 rounded hover:bg-slate-800 transition-colors"
                  >
                    下載處理後圖片
                  </button>
                )}
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 原始圖片 */}
                <div>
                  <h3 className="text-lg font-medium mb-3 text-center">原始圖片</h3>
                  <div className="border rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                       onClick={() => openFullscreen('original')}>
                    <img
                      src={originalImage}
                      alt="原始圖片"
                      className="w-full h-auto max-h-96 object-contain"
                    />
                  </div>
                  <p className="text-xs text-gray-500 text-center mt-2">點擊圖片放大檢視</p>
                </div>

                {/* 處理後圖片 */}
                <div>
                  <h3 className="text-lg font-medium mb-3 text-center">
                    處理後圖片
                    {showPerspectiveEditor && <span className="text-blue-600 text-sm ml-2">(透視校正)</span>}
                  </h3>
                  <div className="border rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                       onClick={() => openFullscreen('processed')}>
                    {/* 基礎處理結果 */}
                    <canvas
                      ref={canvasRef}
                      className="w-full h-auto max-h-96 object-contain"
                      style={{ 
                        maxWidth: '100%', 
                        height: 'auto',
                        display: showPerspectiveEditor ? 'none' : 'block'
                      }}
                    />
                    {/* 最終輸出結果 */}
                    <canvas
                      ref={finalOutputRef}
                      className="w-full h-auto max-h-96 object-contain"
                      style={{ 
                        maxWidth: '100%', 
                        height: 'auto',
                        display: showPerspectiveEditor ? 'block' : 'none'
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 text-center mt-2">點擊圖片放大檢視</p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* 全螢幕檢視 Modal */}
        {fullscreenImage && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
            onClick={closeFullscreen}
          >
            <div className="relative max-w-full max-h-full p-4">
              {/* 關閉按鈕 */}
              <button
                onClick={closeFullscreen}
                className="absolute top-4 right-4 z-10 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full p-2 transition-all"
                title="關閉 (ESC)"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* 圖片標題 */}
              <div className="absolute top-4 left-4 z-10 bg-white bg-opacity-20 text-white px-3 py-1 rounded-lg">
                {fullscreenType === 'original' ? '原始圖片' : '處理後圖片'}
              </div>

              {/* 圖片 */}
              <img
                src={fullscreenImage}
                alt={fullscreenType === 'original' ? '原始圖片' : '處理後圖片'}
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />

              {/* 提示文字 */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded">
                點擊背景或按 ESC 關閉
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}